/**
 * UI-create blocklist: entity types / kinds that must NOT show the generic
 * "Create new X" (inline association picker) or "Add" (list header) affordance,
 * even when the backend would technically accept the create.
 *
 * This is a SEPARATE axis from the backend `user_creatable` flag ("may any user
 * principal create this type at all"). Some targets are legitimately created —
 * just not from the generic CRM UI:
 *
 *   - lifecycle / pipeline *stage* (entity kind 'stage'): created only through
 *     pipeline configuration, never as a free-standing "new stage" from a
 *     record's stage cell.
 *   - the *user* (`ahoy_user`): people join via invitation, not a "create user"
 *     button on an owner cell.
 *
 * Only creation is gated here. Clearing is a different rule and is unaffected:
 * an owner *can* be cleared from a cell (users are optional on a record), a
 * stage *cannot* (every record always sits on one) — that lives in the list
 * page's `allowClear` computation.
 */

// Blocked by entity kind (stage types are user-defined, matched by kind).
const NON_UI_CREATABLE_ENTITY_KINDS = new Set<string>(['stage']);
// Blocked by fixed entity type name.
const NON_UI_CREATABLE_ENTITY_TYPES = new Set<string>(['ahoy_user']);

/**
 * Whether the generic CRM UI should offer to create this entity type.
 *
 * @param entityTypeName the target entity type (e.g. 'ahoy_user', a custom type)
 * @param entityKinds the target type's entity_kind tuple (e.g. ['stage'])
 * @returns false when the target is on the UI-create blocklist by type or kind
 */
export function isUiCreatableEntityType(
  entityTypeName: string | null | undefined,
  entityKinds: readonly string[] = []
): boolean {
  if (
    entityTypeName != null &&
    NON_UI_CREATABLE_ENTITY_TYPES.has(entityTypeName)
  ) {
    return false;
  }
  return !entityKinds.some(kind => NON_UI_CREATABLE_ENTITY_KINDS.has(kind));
}
